import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useFinance } from "./FinanceContext";
import { sum, sumTo } from "./data/model";
import { MONTHS, SEED } from "./data/seedData";
import { AnnualBar } from "./components/AnnualBar";
import { KpiGrid, type KpiItem } from "./components/KpiGrid";

export function RevenuePage() {
  const { model, fmt, toDisp, editRev, selMonth, has } = useFinance();
  const editable = has("revenue.edit");
  const gP = model.revenue.reduce((s, r) => s + sum(r.planned), 0);
  const gA = model.revenue.reduce((s, r) => s + sumTo(r.actual, selMonth), 0);
  const expA = model.items.reduce((s, i) => s + sumTo(i.actual, selMonth), 0);

  const kpis: KpiItem[] = [
    { label: "Planned Revenue", value: fmt(gP) },
    { label: "Actual (YTD)", value: fmt(gA) },
    { label: "Revenue vs Plan", value: (gP ? (gA / gP) * 100 : 0).toFixed(0) + "%" },
    { label: "Gross Profit (YTD)", value: fmt(gA - expA), tone: gA - expA >= 0 ? "pos" : "neg" },
  ];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Revenue Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Revenue streams by month · {SEED.fiscalYear}
      </Typography>
      <AnnualBar
        extra={[
          ["YTD Revenue", fmt(gA)],
          ["Gross Profit (YTD)", fmt(gA - expA)],
        ]}
      />
      <KpiGrid items={kpis} />
      <TableContainer sx={{ maxHeight: "65vh" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Revenue Stream</TableCell>
              {MONTHS.map((m) => (
                <TableCell key={m} align="right">
                  {m}
                </TableCell>
              ))}
              <TableCell align="right">FY Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {model.revenue.map((r) => (
              <TableRow key={r.id}>
                <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                {MONTHS.map((_m, i) => (
                  <TableCell key={i} align="right">
                    {editable ? (
                      <TextField
                        type="number"
                        size="small"
                        variant="standard"
                        value={toDisp(r.planned[i] ?? 0)}
                        onChange={(e) => editRev(r.id, "planned", i, e.target.value)}
                        sx={{ width: 90 }}
                      />
                    ) : (
                      fmt(r.planned[i] ?? 0)
                    )}
                  </TableCell>
                ))}
                <TableCell align="right">{fmt(sum(r.planned))}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ "& td": { fontWeight: 800, borderTop: 2, borderColor: "divider" } }}>
              <TableCell>TOTAL REVENUE</TableCell>
              {MONTHS.map((_m, i) => (
                <TableCell key={i} align="right">
                  {fmt(model.revenue.reduce((s, r) => s + (r.planned[i] ?? 0), 0))}
                </TableCell>
              ))}
              <TableCell align="right">{fmt(gP)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
